import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { DynamicTable } from '../common/DynamicTable';
import { MessageSquare, Plus, RefreshCw, Search, Loader2, Users, Megaphone, Lock, ShieldQuestion, LogIn, Globe, Clock, AlertTriangle, Ban, EyeOff } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { instanciaService } from '../../services/instanciaService';
import socketService from '../../services/socketService';
import api from '../../services/api';
import { grupoService, Grupo } from '../../services/grupoService';
import { CriarGrupoDialog } from './CriarGrupoDialog';
import { GrupoDetalheDialog } from './GrupoDetalheDialog';
import { EntrarGrupoDialog } from './EntrarGrupoDialog';
import { ComunidadesManager } from '../comunidades/ComunidadesManager';

interface InstanciaOption {
  id: string;
  nome: string;
  tipoApi: 'UAZAPI' | 'META';
  /** Meta em coexistência com o app WhatsApp Business: a Groups API não funciona */
  isOnBizApp?: boolean;
}

const TODAS_INSTANCIAS = '__todas__';

export const GruposManager: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [instancias, setInstancias] = useState<InstanciaOption[]>([]);
  const [instanciaFiltro, setInstanciaFiltro] = useState<string>(TODAS_INSTANCIAS);

  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  // Termo efetivamente consultado: espera o usuário parar de digitar
  const [buscaAplicada, setBuscaAplicada] = useState('');
  // Só a resposta da última requisição atualiza a lista (as anteriores podem chegar fora de ordem)
  const ultimaRequisicao = useRef(0);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [criarDialogOpen, setCriarDialogOpen] = useState(false);
  const [entrarDialogOpen, setEntrarDialogOpen] = useState(false);
  const [grupoSelecionadoId, setGrupoSelecionadoId] = useState<string | null>(null);
  const [detalheDialogOpen, setDetalheDialogOpen] = useState(false);

  const instanciaSelecionada = instancias.find((i) => i.id === instanciaFiltro);
  const instanciasUazapi = instancias.filter((i) => i.tipoApi === 'UAZAPI');
  const instanciasMeta = instancias.filter((i) => i.tipoApi === 'META');
  const selecaoEhMeta = instanciaSelecionada?.tipoApi === 'META';

  const carregarInstancias = useCallback(async () => {
    try {
      const response: any = await instanciaService.listar();
      // Uazapi (grupos do WhatsApp Web) e Meta (Groups API do número oficial)
      const suportadas = (response.data.instancias || [])
        .filter((item: any) => item.tipoApi === 'UAZAPI' || item.tipoApi === 'META')
        .map((item: any) => ({ id: item.id, nome: item.nome || 'Instância sem nome', tipoApi: item.tipoApi, isOnBizApp: !!item.isOnBizApp }));
      setInstancias(suportadas);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setBuscaAplicada(search.trim()), 400);
    return () => clearTimeout(timeout);
  }, [search]);

  const carregarGrupos = useCallback(async () => {
    const requisicao = ++ultimaRequisicao.current;
    setLoading(true);
    try {
      const response = await grupoService.listar({
        instanciaUazapiId: instanciaSelecionada?.tipoApi === 'UAZAPI' ? instanciaSelecionada.id : undefined,
        instanciaMetaId: instanciaSelecionada?.tipoApi === 'META' ? instanciaSelecionada.id : undefined,
        search: buscaAplicada || undefined,
        page: page + 1,
        pageSize: rowsPerPage,
      });
      if (requisicao !== ultimaRequisicao.current) return;
      setGrupos(response.data.grupos || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error: any) {
      if (requisicao !== ultimaRequisicao.current) return;
      toast({
        title: 'Erro ao carregar grupos',
        description: error?.response?.data?.message || 'Não foi possível carregar a lista de grupos.',
        variant: 'destructive',
      });
    } finally {
      if (requisicao === ultimaRequisicao.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanciaFiltro, instancias, buscaAplicada, page, rowsPerPage]);

  useEffect(() => { carregarInstancias(); }, [carregarInstancias]);
  useEffect(() => { carregarGrupos(); }, [carregarGrupos]);

  // Grupos Meta mudam por webhook (criação concluída, entradas/saídas, suspensão...): atualiza a lista
  useEffect(() => {
    const aoAtualizar = () => { carregarGrupos(); };
    socketService.on('grupo_atualizado', aoAtualizar);
    return () => socketService.off('grupo_atualizado', aoAtualizar);
  }, [carregarGrupos]);

  const instanciaNome = (grupo: Grupo) => {
    const id = grupo.instanciaMetaId || grupo.instanciaUazapiId;
    const nome = instancias.find((i) => i.id === id)?.nome || '—';
    return grupo.tipoApi === 'META' ? `${nome} · API oficial` : nome;
  };

  const handleSincronizar = async () => {
    if (instanciaFiltro === TODAS_INSTANCIAS) {
      toast({ title: 'Selecione uma instância', description: 'Escolha uma instância específica para sincronizar os grupos.', variant: 'destructive' });
      return;
    }
    setSyncing(true);
    try {
      await grupoService.sincronizar(
        selecaoEhMeta ? { instanciaMetaId: instanciaFiltro } : { instanciaUazapiId: instanciaFiltro },
        search.trim() || undefined
      );
      toast({ title: 'Grupos sincronizados', description: 'A lista foi atualizada com os dados mais recentes do WhatsApp.' });
      await carregarGrupos();
    } catch (error: any) {
      toast({
        title: 'Erro ao sincronizar',
        description: error?.response?.data?.message || 'Não foi possível sincronizar os grupos.',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleGerenciar = (grupo: Grupo) => {
    setGrupoSelecionadoId(grupo.id);
    setDetalheDialogOpen(true);
  };

  const handleGrupoAtualizado = (grupoAtualizado: Grupo) => {
    setGrupos((prev) => prev.map((g) => (g.id === grupoAtualizado.id ? { ...g, ...grupoAtualizado } : g)));
  };

  const handleGrupoSaiu = (grupoId: string) => {
    setGrupos((prev) => prev.filter((g) => g.id !== grupoId));
    setTotal((prev) => Math.max(0, prev - 1));
  };

  // Abre (ou cria) a conversa do grupo e leva o atendente para ela
  const handleIniciarConversa = async (grupo: Grupo) => {
    const instanciaId = grupo.instanciaMetaId || grupo.instanciaUazapiId;
    if (grupo.statusCriacao === 'pendente' || !grupo.jid || grupo.jid.startsWith('pending_')) {
      toast({ title: 'Grupo ainda em criação', description: 'Aguarde a confirmação da Meta para iniciar a conversa.', variant: 'destructive' });
      return;
    }
    try {
      const { data } = await api.post('/chats/find-or-create', {
        telefone: grupo.jid.includes('@') ? grupo.jid : `${grupo.jid}@g.us`,
        instanciaId,
      });
      if (data?.alreadyAssigned || data?.openOnOtherInstance) {
        toast({ title: data.message || 'Já existe um atendimento para este grupo', variant: 'destructive' });
        return;
      }
      navigate(`/dashboard?ticketId=${data.id}`);
    } catch (error: any) {
      toast({
        title: 'Erro ao iniciar conversa',
        description: error?.response?.data?.message || 'Não foi possível abrir a conversa do grupo.',
        variant: 'destructive',
      });
    }
  };

  const handleGrupoCriado = (grupo: Grupo) => {
    setPage(0);
    carregarGrupos();
    // Grupo Uazapi nasce ativo: já abre a conversa para enviar a primeira mensagem (Meta é assíncrona)
    if (grupo?.statusCriacao !== 'pendente' && grupo?.jid) {
      handleIniciarConversa(grupo);
    }
  };

  const handleGrupoEntrou = () => {
    setPage(0);
    carregarGrupos();
  };

  const columns = [
    {
      id: 'nome' as const,
      label: 'Grupo',
      format: (_: string, row: Grupo) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
              <Users className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{row.nome}</p>
            {instanciaFiltro === TODAS_INSTANCIAS && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{instanciaNome(row)}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'totalParticipantes' as const,
      label: 'Participantes',
      align: 'center' as const,
      format: (_: number, row: Grupo) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">{row._count?.participantes ?? row.totalParticipantes}</span>
      ),
    },
    {
      id: 'configuracoes' as any,
      label: 'Configurações',
      format: (_: any, row: Grupo) => (
        <div className="flex flex-wrap gap-1">
          {row.statusCriacao === 'pendente' && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300" title="Aguardando a confirmação da Meta">
              <Clock className="h-3 w-3" /> Criando...
            </span>
          )}
          {row.statusCriacao === 'falhou' && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" title={row.erroCriacao || 'Falha na criação'}>
              <AlertTriangle className="h-3 w-3" /> Falhou
            </span>
          )}
          {row.suspenso && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
              <Ban className="h-3 w-3" /> Suspenso
            </span>
          )}
          {row.mostrarNaGestao === false && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300" title="As mensagens deste grupo não são gravadas no sistema">
              <EyeOff className="h-3 w-3" /> Fora dos tickets
            </span>
          )}
          {row.isAnnounce && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
              <Megaphone className="h-3 w-3" /> Anúncio
            </span>
          )}
          {row.isLocked && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300">
              <Lock className="h-3 w-3" /> Bloqueado
            </span>
          )}
          {row.isJoinApprovalRequired && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              <ShieldQuestion className="h-3 w-3" /> Aprovação
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'actions' as any,
      label: 'Ações',
      align: 'right' as const,
      format: (_: any, row: Grupo) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => handleIniciarConversa(row)} title="Iniciar conversa no grupo">
            <MessageSquare className="h-4 w-4 mr-1" /> Conversar
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleGerenciar(row)}>
            Gerenciar
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Tabs defaultValue="grupos" className="space-y-4">
      <TabsList className="bg-gray-200 dark:bg-slate-800 dark:border dark:border-slate-700">
        <TabsTrigger value="grupos" className="dark:text-gray-300 dark:data-[state=active]:bg-slate-600 dark:data-[state=active]:text-white">
          <Users className="h-4 w-4 mr-2" /> Grupos
        </TabsTrigger>
        <TabsTrigger value="comunidades" className="dark:text-gray-300 dark:data-[state=active]:bg-slate-600 dark:data-[state=active]:text-white">
          <Globe className="h-4 w-4 mr-2" /> Comunidades
        </TabsTrigger>
      </TabsList>

      <TabsContent value="grupos" className="space-y-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Grupos</h2>
            <p className="text-sm text-muted-foreground dark:text-gray-400">
              Crie e administre grupos do WhatsApp (Uazapi e API oficial da Meta).
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={instanciaFiltro} onValueChange={(v) => { setInstanciaFiltro(v); setPage(0); }}>
              <SelectTrigger className="w-[220px] bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 dark:text-white">
                <SelectValue placeholder="Instância" />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100">
                <SelectItem value={TODAS_INSTANCIAS}>Todas as instâncias</SelectItem>
                {instanciasUazapi.map((inst) => (
                  <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                ))}
                {instanciasMeta.map((inst) => (
                  <SelectItem key={inst.id} value={inst.id}>
                    {inst.nome} (API oficial{inst.isOnBizApp ? ' · coexistência: sem grupos' : ''})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={handleSincronizar} disabled={syncing}>
              {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Sincronizar
            </Button>

            <Button
              variant="outline"
              onClick={() => setEntrarDialogOpen(true)}
              disabled={instanciasUazapi.length === 0 || selecaoEhMeta}
              title={selecaoEhMeta ? 'A API oficial não permite entrar em grupos existentes: grupos são criados pela empresa' : undefined}
            >
              <LogIn className="mr-2 h-4 w-4" /> Entrar em grupo
            </Button>

            <Button
              onClick={() => setCriarDialogOpen(true)}
              disabled={instancias.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Plus className="mr-2 h-4 w-4" /> Novo Grupo
            </Button>
          </div>
        </div>

        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Pesquisar grupos pelo nome..."
            className="pl-9 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400"
          />
        </div>

        {instancias.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground dark:text-gray-400">
            Nenhuma instância Uazapi ou Meta conectada. Configure uma instância em Configurações → Instâncias para gerenciar grupos.
          </p>
        )}

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden shadow-sm">
          <DynamicTable
            columns={columns}
            data={grupos}
            loading={loading}
            total={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onSort={() => {}}
            onSearch={() => {}}
            showSearch={false}
            noDataText="Nenhum grupo encontrado. Clique em Sincronizar ou crie um novo grupo."
          />
        </div>

        <CriarGrupoDialog
          open={criarDialogOpen}
          onOpenChange={setCriarDialogOpen}
          instancias={instancias}
          instanciaPadraoId={instanciaFiltro !== TODAS_INSTANCIAS ? instanciaFiltro : undefined}
          onCriado={handleGrupoCriado}
        />

        <EntrarGrupoDialog
          open={entrarDialogOpen}
          onOpenChange={setEntrarDialogOpen}
          instancias={instanciasUazapi}
          instanciaPadraoId={instanciaFiltro !== TODAS_INSTANCIAS ? instanciaFiltro : undefined}
          onEntrou={handleGrupoEntrou}
        />

        <GrupoDetalheDialog
          open={detalheDialogOpen}
          onOpenChange={setDetalheDialogOpen}
          grupoId={grupoSelecionadoId}
          onAtualizado={handleGrupoAtualizado}
          onSaiu={handleGrupoSaiu}
        />
      </TabsContent>

      <TabsContent value="comunidades">
        <ComunidadesManager />
      </TabsContent>
    </Tabs>
  );
};

export default GruposManager;
